import { z } from "zod";
// Health check response schema
export const HealthResponseSchema = z.object({
    status: z.literal("ok"),
    environment: z.string(),
    timestamp: z.string(),
});
// API info response schema
export const ApiInfoSchema = z.object({
    name: z.string(),
    version: z.string(),
});
// Auth schemas
export const LoginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8),
});
export const RegisterRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8),
    name: z.string().min(1).max(255),
    tenantName: z.string().min(1).max(255),
});
export const AuthResponseSchema = z.object({
    token: z.string(),
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        name: z.string(),
        role: z.enum(["owner", "admin", "staff"]),
        tenantId: z.string().uuid(),
    }),
});
export const MeResponseSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    role: z.enum(["owner", "admin", "staff"]),
    tenantId: z.string().uuid(),
    tenantName: z.string(),
});
// Password reset schemas
export const ForgotPasswordRequestSchema = z.object({
    email: z.string().email(),
});
export const ResetPasswordRequestSchema = z.object({
    token: z.string().min(1),
    password: z.string().min(8),
});
export const MessageResponseSchema = z.object({
    message: z.string(),
});
// =============================================================================
// Client Schemas
// =============================================================================
export const ClientAddressSchema = z.object({
    street: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    country: z.string().optional(),
});
export const ClientSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    email: z.string().email(),
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    phone: z.string().max(50).optional().nullable(),
    address: ClientAddressSchema.optional().nullable(),
    timezone: z.string().max(50).optional().nullable(),
    notes: z.string().optional().nullable(),
    tags: z.array(z.string()).optional().nullable(),
    isBanned: z.boolean(),
    banReason: z.string().optional().nullable(),
    lastAppointmentAt: z.string().datetime().optional().nullable(),
    totalAppointments: z.number().int(),
    totalSpent: z.string(), // decimal comes as string from DB
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateClientSchema = ClientSchema.omit({
    id: true,
    tenantId: true,
    lastAppointmentAt: true,
    totalAppointments: true,
    totalSpent: true,
    createdAt: true,
    updatedAt: true,
}).extend({
    isBanned: z.boolean().optional(), // defaults to false in DB
});
export const UpdateClientSchema = CreateClientSchema.partial();
// =============================================================================
// Dog Schemas
// =============================================================================
export const DogGenderSchema = z.enum(["male", "female", "unknown"]);
export const DogVaccinationSchema = z.object({
    name: z.string(),
    date: z.string(), // ISO date string
    expiresAt: z.string().optional(), // ISO date string
});
export const DogSchema = z.object({
    id: z.string().uuid(),
    clientId: z.string().uuid(),
    tenantId: z.string().uuid(),
    name: z.string().min(1).max(100),
    breed: z.string().max(100).optional().nullable(),
    color: z.string().max(50).optional().nullable(),
    weight: z.string().optional().nullable(), // decimal comes as string from DB
    dateOfBirth: z.string().optional().nullable(), // date string (YYYY-MM-DD)
    gender: DogGenderSchema.optional().nullable(),
    isNeutered: z.boolean().optional().nullable(),
    microchipId: z.string().max(50).optional().nullable(),
    notes: z.string().optional().nullable(),
    medicalNotes: z.string().optional().nullable(),
    behavioralNotes: z.string().optional().nullable(),
    temperament: z.string().optional().nullable(),
    groomingNotes: z.string().optional().nullable(),
    trainingNotes: z.string().optional().nullable(),
    commandsLearned: z.array(z.string()).optional().nullable(),
    vaccinations: z.array(DogVaccinationSchema).optional().nullable(),
    profilePhotoUrl: z.string().max(500).optional().nullable(),
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateDogSchema = DogSchema.omit({
    id: true,
    tenantId: true,
    createdAt: true,
    updatedAt: true,
}).extend({
    isActive: z.boolean().optional(), // defaults to true in DB
});
export const UpdateDogSchema = CreateDogSchema.partial().omit({
    clientId: true, // clientId should not be updated
});
// =============================================================================
// Calendar Schemas
// =============================================================================
export const CalendarTypeSchema = z.enum(["staff", "resource"]);
export const CalendarSettingsSchema = z.object({
    defaultDuration: z.number().int().positive().optional(),
    bufferBetweenAppointments: z.number().int().min(0).optional(),
}).catchall(z.unknown());
export const CalendarSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    name: z.string().min(1).max(100),
    calendarType: CalendarTypeSchema,
    description: z.string().optional().nullable(),
    color: z.string().max(20).optional().nullable(),
    settings: CalendarSettingsSchema.optional().nullable(),
    timezone: z.string().max(50),
    defaultDuration: z.number().int().positive(),
    bufferBefore: z.number().int().min(0),
    bufferAfter: z.number().int().min(0),
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateCalendarSchema = CalendarSchema.omit({
    id: true,
    tenantId: true,
    createdAt: true,
    updatedAt: true,
}).extend({
    calendarType: CalendarTypeSchema.optional(), // defaults to 'staff' in DB
    settings: CalendarSettingsSchema.optional(), // defaults to {} in DB
    timezone: z.string().max(50).optional(), // defaults to UTC in DB
    defaultDuration: z.number().int().positive().optional(), // defaults to 60 in DB
    bufferBefore: z.number().int().min(0).optional(), // defaults to 0 in DB
    bufferAfter: z.number().int().min(0).optional(), // defaults to 0 in DB
    isActive: z.boolean().optional(), // defaults to true in DB
});
export const UpdateCalendarSchema = CreateCalendarSchema.partial();
// =============================================================================
// Appointment Type Schemas
// =============================================================================
export const DepositTypeSchema = z.enum(["fixed", "percentage"]);
export const AppointmentTypeSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    name: z.string().min(1).max(100),
    description: z.string().optional().nullable(),
    duration: z.number().int().positive(),
    price: z.string(), // decimal comes as string from DB
    color: z.string().max(20).optional().nullable(),
    category: z.string().max(50).optional().nullable(),
    requiresDog: z.boolean(),
    maxDogsPerAppointment: z.number().int().positive(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
    // Buffer time settings
    bufferBeforeMinutes: z.number().int().min(0),
    bufferAfterMinutes: z.number().int().min(0),
    // Deposit settings
    depositAmount: z.string().optional().nullable(), // decimal comes as string from DB
    depositType: DepositTypeSchema.optional().nullable(),
    // Visibility
    isPublic: z.boolean(),
    // Group class settings
    isGroupClass: z.boolean(),
    maxParticipants: z.number().int().positive().optional().nullable(),
    // Program settings
    isProgram: z.boolean(),
    // Resource associations
    calendarIds: z.array(z.string().uuid()).optional().nullable(),
    formIds: z.array(z.string().uuid()).optional().nullable(),
    resourceIds: z.array(z.string().uuid()).optional().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateAppointmentTypeSchema = AppointmentTypeSchema.omit({
    id: true,
    tenantId: true,
    createdAt: true,
    updatedAt: true,
}).extend({
    requiresDog: z.boolean().optional(), // defaults to true in DB
    maxDogsPerAppointment: z.number().int().positive().optional(), // defaults to 1 in DB
    isActive: z.boolean().optional(), // defaults to true in DB
    sortOrder: z.number().int().optional(), // defaults to 0 in DB
    // Buffer times default to 0 in DB
    bufferBeforeMinutes: z.number().int().min(0).optional(),
    bufferAfterMinutes: z.number().int().min(0).optional(),
    // isPublic defaults to true in DB
    isPublic: z.boolean().optional(),
    // Group class defaults to false in DB
    isGroupClass: z.boolean().optional(),
    // Program defaults to false in DB
    isProgram: z.boolean().optional(),
});
export const UpdateAppointmentTypeSchema = CreateAppointmentTypeSchema.partial();
// =============================================================================
// Appointment Schemas
// =============================================================================
export const AppointmentStatusSchema = z.enum([
    "scheduled",
    "confirmed",
    "in_progress",
    "completed",
    "cancelled",
    "no_show",
]);
export const LocationTypeSchema = z.enum([
    "facility",
    "client_home",
    "saved_location",
    "virtual",
]);
export const BookedBySchema = z.enum([
    "staff",
    "client",
    "system",
]);
export const PaymentStatusSchema = z.enum([
    "unpaid",
    "partial",
    "paid",
    "refunded",
]);
export const AppointmentSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    calendarId: z.string().uuid(),
    clientId: z.string().uuid(),
    dogId: z.string().uuid().optional().nullable(),
    appointmentTypeId: z.string().uuid().optional().nullable(),
    status: AppointmentStatusSchema,
    startTime: z.string().datetime(),
    endTime: z.string().datetime(),
    price: z.string().optional().nullable(), // decimal comes as string from DB
    notes: z.string().max(2000).optional().nullable(),
    cancellationReason: z.string().max(500).optional().nullable(),
    cancelledAt: z.string().datetime().optional().nullable(),
    cancelledBy: z.string().uuid().optional().nullable(),
    completedAt: z.string().datetime().optional().nullable(),
    // Location fields
    locationType: LocationTypeSchema,
    savedLocationId: z.string().uuid().optional().nullable(),
    resourceId: z.string().uuid().optional().nullable(),
    // Booking source tracking
    bookedBy: BookedBySchema,
    bookedByUserId: z.string().uuid().optional().nullable(),
    // Override fields
    isOverride: z.boolean(),
    overrideReason: z.string().optional().nullable(),
    // Addon references
    addonIds: z.array(z.string().uuid()).optional().nullable(),
    // Payment fields
    priceTotal: z.string().optional().nullable(), // decimal comes as string from DB
    pricePaid: z.string().optional().nullable(), // decimal comes as string from DB
    paymentStatus: PaymentStatusSchema,
    // Staff-only notes
    internalNotes: z.string().optional().nullable(),
    // Label references
    labelIds: z.array(z.string().uuid()).optional().nullable(),
    // Explicit no-show flag
    noShow: z.boolean(),
    // External calendar sync
    externalCalendarEventId: z.string().max(255).optional().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateAppointmentSchema = z
    .object({
    calendarId: z.string().uuid(),
    clientId: z.string().uuid(),
    dogId: z.string().uuid().optional(),
    appointmentTypeId: z.string().uuid(),
    startTime: z.string().datetime(),
    endTime: z.string().datetime(),
    price: z.string().optional(), // optional price override
    notes: z.string().max(2000).optional(),
    // Location fields (optional - defaults to 'facility')
    locationType: LocationTypeSchema.optional(),
    savedLocationId: z.string().uuid().optional(),
    resourceId: z.string().uuid().optional(),
    // Booking source tracking (optional - defaults to 'staff')
    bookedBy: BookedBySchema.optional(),
    bookedByUserId: z.string().uuid().optional(),
    // Override fields (optional - defaults to false)
    isOverride: z.boolean().optional(),
    overrideReason: z.string().optional(),
    // Addon references
    addonIds: z.array(z.string().uuid()).optional(),
    // Payment fields (optional - defaults to 'unpaid')
    priceTotal: z.string().optional(),
    pricePaid: z.string().optional(),
    paymentStatus: PaymentStatusSchema.optional(),
    // Staff-only notes
    internalNotes: z.string().optional(),
    // Label references
    labelIds: z.array(z.string().uuid()).optional(),
    // Explicit no-show flag (optional - defaults to false)
    noShow: z.boolean().optional(),
    // External calendar sync
    externalCalendarEventId: z.string().max(255).optional(),
})
    .refine((data) => new Date(data.endTime) > new Date(data.startTime), {
    message: "End time must be after start time",
    path: ["endTime"],
});
export const UpdateAppointmentSchema = z
    .object({
    calendarId: z.string().uuid().optional(),
    clientId: z.string().uuid().optional(),
    dogId: z.string().uuid().optional().nullable(),
    appointmentTypeId: z.string().uuid().optional(),
    startTime: z.string().datetime().optional(),
    endTime: z.string().datetime().optional(),
    price: z.string().optional().nullable(),
    notes: z.string().max(2000).optional().nullable(),
    status: AppointmentStatusSchema.optional(),
    cancellationReason: z.string().max(500).optional().nullable(),
    // Location fields
    locationType: LocationTypeSchema.optional(),
    savedLocationId: z.string().uuid().optional().nullable(),
    resourceId: z.string().uuid().optional().nullable(),
    // Booking source tracking
    bookedBy: BookedBySchema.optional(),
    bookedByUserId: z.string().uuid().optional().nullable(),
    // Override fields
    isOverride: z.boolean().optional(),
    overrideReason: z.string().optional().nullable(),
    // Addon references
    addonIds: z.array(z.string().uuid()).optional().nullable(),
    // Payment fields
    priceTotal: z.string().optional().nullable(),
    pricePaid: z.string().optional().nullable(),
    paymentStatus: PaymentStatusSchema.optional(),
    // Staff-only notes
    internalNotes: z.string().optional().nullable(),
    // Label references
    labelIds: z.array(z.string().uuid()).optional().nullable(),
    // Explicit no-show flag
    noShow: z.boolean().optional(),
    // External calendar sync
    externalCalendarEventId: z.string().max(255).optional().nullable(),
})
    .refine((data) => {
    // Only validate if both times are provided
    if (data.startTime && data.endTime) {
        return new Date(data.endTime) > new Date(data.startTime);
    }
    return true;
}, {
    message: "End time must be after start time",
    path: ["endTime"],
});
// =============================================================================
// Availability Rule Schemas (recurring weekly availability)
// =============================================================================
// Helper for HH:MM time format validation
const TimeStringSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, {
    message: "Time must be in HH:MM format (00:00-23:59)",
});
// Helper for YYYY-MM-DD date format validation
const DateStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: "Date must be in YYYY-MM-DD format",
});
export const AvailabilityRuleSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    calendarId: z.string().uuid(),
    dayOfWeek: z.number().int().min(0).max(6), // 0 = Sunday, 6 = Saturday
    startTime: TimeStringSchema,
    endTime: TimeStringSchema,
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateAvailabilityRuleSchema = z
    .object({
    calendarId: z.string().uuid(),
    dayOfWeek: z.number().int().min(0).max(6),
    startTime: TimeStringSchema,
    endTime: TimeStringSchema,
    isActive: z.boolean().optional(), // defaults to true in DB
})
    .refine((data) => data.endTime > data.startTime, {
    message: "End time must be after start time",
    path: ["endTime"],
});
export const UpdateAvailabilityRuleSchema = z
    .object({
    calendarId: z.string().uuid().optional(),
    dayOfWeek: z.number().int().min(0).max(6).optional(),
    startTime: TimeStringSchema.optional(),
    endTime: TimeStringSchema.optional(),
    isActive: z.boolean().optional(),
})
    .refine((data) => {
    // Only validate if both times are provided
    if (data.startTime && data.endTime) {
        return data.endTime > data.startTime;
    }
    return true;
}, {
    message: "End time must be after start time",
    path: ["endTime"],
});
// =============================================================================
// Availability Override Schemas (specific date overrides)
// =============================================================================
export const AvailabilityOverrideSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    calendarId: z.string().uuid(),
    date: DateStringSchema, // YYYY-MM-DD
    startTime: TimeStringSchema.optional().nullable(), // null means unavailable all day
    endTime: TimeStringSchema.optional().nullable(),
    reason: z.string().max(255).optional().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateAvailabilityOverrideSchema = z
    .object({
    calendarId: z.string().uuid(),
    date: DateStringSchema,
    startTime: TimeStringSchema.optional().nullable(),
    endTime: TimeStringSchema.optional().nullable(),
    reason: z.string().max(255).optional(),
})
    .refine((data) => {
    // If both times are provided, end must be after start
    if (data.startTime && data.endTime) {
        return data.endTime > data.startTime;
    }
    return true;
}, {
    message: "End time must be after start time",
    path: ["endTime"],
});
export const UpdateAvailabilityOverrideSchema = z
    .object({
    calendarId: z.string().uuid().optional(),
    date: DateStringSchema.optional(),
    startTime: TimeStringSchema.optional().nullable(),
    endTime: TimeStringSchema.optional().nullable(),
    reason: z.string().max(255).optional().nullable(),
})
    .refine((data) => {
    // If both times are provided, end must be after start
    if (data.startTime && data.endTime) {
        return data.endTime > data.startTime;
    }
    return true;
}, {
    message: "End time must be after start time",
    path: ["endTime"],
});
// =============================================================================
// Blocked Time Schemas (specific time blocks that are unavailable)
// =============================================================================
export const BlockedTimeSourceSchema = z.enum([
    "manual",
    "calendar_sync",
    "appointment",
]);
export const BlockedTimeSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    calendarId: z.string().uuid(),
    startTime: z.string().datetime(), // ISO datetime
    endTime: z.string().datetime(),
    reason: z.string().max(255).optional().nullable(),
    isRecurring: z.boolean(),
    recurrenceRule: z.string().optional().nullable(),
    source: BlockedTimeSourceSchema,
    externalEventId: z.string().max(255).optional().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateBlockedTimeSchema = z
    .object({
    calendarId: z.string().uuid(),
    startTime: z.string().datetime(),
    endTime: z.string().datetime(),
    reason: z.string().max(255).optional(),
    isRecurring: z.boolean().optional(), // defaults to false in DB
    recurrenceRule: z.string().optional(),
    source: BlockedTimeSourceSchema.optional(), // defaults to 'manual' in DB
    externalEventId: z.string().max(255).optional(),
})
    .refine((data) => new Date(data.endTime) > new Date(data.startTime), {
    message: "End time must be after start time",
    path: ["endTime"],
});
export const UpdateBlockedTimeSchema = z
    .object({
    calendarId: z.string().uuid().optional(),
    startTime: z.string().datetime().optional(),
    endTime: z.string().datetime().optional(),
    reason: z.string().max(255).optional().nullable(),
    isRecurring: z.boolean().optional(),
    recurrenceRule: z.string().optional().nullable(),
    source: BlockedTimeSourceSchema.optional(),
    externalEventId: z.string().max(255).optional().nullable(),
})
    .refine((data) => {
    // Only validate if both times are provided
    if (data.startTime && data.endTime) {
        return new Date(data.endTime) > new Date(data.startTime);
    }
    return true;
}, {
    message: "End time must be after start time",
    path: ["endTime"],
});
// =============================================================================
// Available Slot Schema (for API responses showing available time slots)
// =============================================================================
export const AvailableSlotSchema = z.object({
    startTime: z.string().datetime(), // ISO datetime
    endTime: z.string().datetime(),
});
// =============================================================================
// Resource Schemas (capacity limiters - invisible to clients)
// =============================================================================
export const ResourceSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    name: z.string().min(1).max(255),
    description: z.string().optional().nullable(),
    quantity: z.number().int().positive(),
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateResourceSchema = z.object({
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    quantity: z.number().int().positive().optional(), // defaults to 1 in DB
    isActive: z.boolean().optional(), // defaults to true in DB
});
export const UpdateResourceSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().optional().nullable(),
    quantity: z.number().int().positive().optional(),
    isActive: z.boolean().optional(),
});
// =============================================================================
// Label Schemas (tags for appointment categorization/filtering)
// =============================================================================
export const LabelSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    name: z.string().min(1).max(100),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/), // hex color
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateLabelSchema = z.object({
    name: z.string().min(1).max(100),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(), // defaults to purple
});
export const UpdateLabelSchema = CreateLabelSchema.partial();
// =============================================================================
// Saved Location Schemas (address book for common meeting spots)
// =============================================================================
export const LocationAddressSchema = z.object({
    street: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    country: z.string().optional(),
    lat: z.number().optional(),
    lng: z.number().optional(),
});
export const SavedLocationSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    name: z.string().min(1).max(255),
    address: LocationAddressSchema.optional().nullable(),
    googleMapsUrl: z.string().max(500).optional().nullable(),
    notes: z.string().optional().nullable(),
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateSavedLocationSchema = z.object({
    name: z.string().min(1).max(255),
    address: LocationAddressSchema.optional(),
    googleMapsUrl: z.string().max(500).optional(),
    notes: z.string().optional(),
    isActive: z.boolean().optional(), // defaults to true in DB
});
export const UpdateSavedLocationSchema = CreateSavedLocationSchema.partial();
// =============================================================================
// Appointment Addon Schemas (add-on services for appointments)
// =============================================================================
export const AppointmentAddonSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    appointmentTypeId: z.string().uuid().optional().nullable(),
    name: z.string().min(1).max(255),
    description: z.string().optional().nullable(),
    durationMinutes: z.number().int().min(0),
    price: z.string(), // decimal comes as string from DB
    isActive: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateAppointmentAddonSchema = z.object({
    appointmentTypeId: z.string().uuid().optional(),
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    durationMinutes: z.number().int().min(0).optional(),
    price: z.string().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
export const UpdateAppointmentAddonSchema = CreateAppointmentAddonSchema.partial();
// =============================================================================
// Class Series Schemas (multi-week courses)
// =============================================================================
export const ClassSeriesSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    appointmentTypeId: z.string().uuid(),
    name: z.string().min(1).max(255),
    description: z.string().optional().nullable(),
    totalSessions: z.number().int().positive(),
    price: z.string(), // decimal comes as string from DB
    requiresFullEnrollment: z.boolean(),
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateClassSeriesSchema = z.object({
    appointmentTypeId: z.string().uuid(),
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    totalSessions: z.number().int().positive(),
    price: z.string(),
    requiresFullEnrollment: z.boolean().optional(),
    isActive: z.boolean().optional(),
});
export const UpdateClassSeriesSchema = CreateClassSeriesSchema.partial().extend({
    description: z.string().optional().nullable(),
});
// =============================================================================
// Group Class Instance Schemas (specific scheduled occurrences of group classes)
// =============================================================================
export const ClassInstanceStatusSchema = z.enum([
    "scheduled",
    "cancelled",
    "completed",
]);
export const GroupClassInstanceSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    appointmentTypeId: z.string().uuid(),
    calendarId: z.string().uuid(),
    seriesId: z.string().uuid().optional().nullable(),
    startTime: z.string().datetime(),
    endTime: z.string().datetime(),
    maxParticipants: z.number().int().positive(),
    currentParticipants: z.number().int().min(0),
    status: ClassInstanceStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateGroupClassInstanceSchema = z
    .object({
    appointmentTypeId: z.string().uuid(),
    calendarId: z.string().uuid(),
    seriesId: z.string().uuid().optional(),
    startTime: z.string().datetime(),
    endTime: z.string().datetime(),
    maxParticipants: z.number().int().positive(),
    status: ClassInstanceStatusSchema.optional(),
})
    .refine((data) => new Date(data.endTime) > new Date(data.startTime), {
    message: "End time must be after start time",
    path: ["endTime"],
});
export const UpdateGroupClassInstanceSchema = z
    .object({
    appointmentTypeId: z.string().uuid().optional(),
    calendarId: z.string().uuid().optional(),
    seriesId: z.string().uuid().optional().nullable(),
    startTime: z.string().datetime().optional(),
    endTime: z.string().datetime().optional(),
    maxParticipants: z.number().int().positive().optional(),
    status: ClassInstanceStatusSchema.optional(),
})
    .refine((data) => {
    // Only validate if both times are provided
    if (data.startTime && data.endTime) {
        return new Date(data.endTime) > new Date(data.startTime);
    }
    return true;
}, {
    message: "End time must be after start time",
    path: ["endTime"],
});
// =============================================================================
// Class Enrollment Schemas (client/dog signups for group class instances)
// =============================================================================
export const EnrollmentStatusSchema = z.enum([
    "enrolled",
    "waitlisted",
    "cancelled",
    "completed",
    "no_show",
]);
export const EnrollmentPaymentStatusSchema = z.enum([
    "unpaid",
    "partial",
    "paid",
    "refunded",
]);
export const ClassEnrollmentSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    classInstanceId: z.string().uuid(),
    clientId: z.string().uuid(),
    dogId: z.string().uuid().optional().nullable(),
    status: EnrollmentStatusSchema,
    paymentStatus: EnrollmentPaymentStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateClassEnrollmentSchema = z.object({
    classInstanceId: z.string().uuid(),
    clientId: z.string().uuid(),
    dogId: z.string().uuid().optional(),
    status: EnrollmentStatusSchema.optional(),
    paymentStatus: EnrollmentPaymentStatusSchema.optional(),
});
export const UpdateClassEnrollmentSchema = z.object({
    status: EnrollmentStatusSchema.optional(),
    paymentStatus: EnrollmentPaymentStatusSchema.optional(),
    dogId: z.string().uuid().optional().nullable(),
});
// =============================================================================
// Dog Service Enrollment Schemas (tracking progress through program services)
// =============================================================================
export const ServiceEnrollmentStatusSchema = z.enum([
    "active",
    "paused",
    "completed",
    "cancelled",
]);
export const DogServiceEnrollmentSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    dogId: z.string().uuid(),
    appointmentTypeId: z.string().uuid(),
    status: ServiceEnrollmentStatusSchema,
    appointmentsCompleted: z.number().int().min(0),
    enrolledAt: z.string().datetime(),
    completedAt: z.string().datetime().optional().nullable(),
    completedByUserId: z.string().uuid().optional().nullable(),
    notes: z.string().optional().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateDogServiceEnrollmentSchema = z.object({
    dogId: z.string().uuid(),
    appointmentTypeId: z.string().uuid(),
    status: ServiceEnrollmentStatusSchema.optional(),
    notes: z.string().optional(),
});
export const UpdateDogServiceEnrollmentSchema = z.object({
    status: ServiceEnrollmentStatusSchema.optional(),
    notes: z.string().optional().nullable(),
});
// =============================================================================
// Dog Vaccination Schemas (vaccination records for dogs)
// =============================================================================
// Helper for YYYY-MM-DD date format validation (reusing pattern from above)
const VaccinationDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: "Date must be in YYYY-MM-DD format",
});
export const DogVaccinationRecordSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    dogId: z.string().uuid(),
    vaccinationType: z.string().min(1).max(100),
    administeredDate: VaccinationDateSchema,
    expirationDate: VaccinationDateSchema.optional().nullable(),
    veterinarian: z.string().max(255).optional().nullable(),
    documentUrl: z.string().max(500).optional().nullable(),
    notes: z.string().optional().nullable(),
    createdAt: z.string().datetime(),
});
export const CreateDogVaccinationSchema = z.object({
    dogId: z.string().uuid(),
    vaccinationType: z.string().min(1).max(100),
    administeredDate: VaccinationDateSchema,
    expirationDate: VaccinationDateSchema.optional(),
    veterinarian: z.string().max(255).optional(),
    documentUrl: z.string().max(500).optional(),
    notes: z.string().optional(),
});
export const UpdateDogVaccinationSchema = z.object({
    vaccinationType: z.string().min(1).max(100).optional(),
    administeredDate: VaccinationDateSchema.optional(),
    expirationDate: VaccinationDateSchema.optional().nullable(),
    veterinarian: z.string().max(255).optional().nullable(),
    documentUrl: z.string().max(500).optional().nullable(),
    notes: z.string().optional().nullable(),
});
// =============================================================================
// Form Schemas (intake form definitions for tenants)
// =============================================================================
export const FormTypeSchema = z.enum([
    "intake",
    "consent",
    "waiver",
    "feedback",
    "custom",
]);
export const FormSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    name: z.string().min(1).max(255),
    description: z.string().optional().nullable(),
    type: z.string().max(50),
    isInternal: z.boolean(),
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateFormSchema = z.object({
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    type: z.string().max(50).optional(), // defaults to 'intake' in DB
    isInternal: z.boolean().optional(), // defaults to false in DB
    isActive: z.boolean().optional(), // defaults to true in DB
});
export const UpdateFormSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().optional().nullable(),
    type: z.string().max(50).optional(),
    isInternal: z.boolean().optional(),
    isActive: z.boolean().optional(),
});
// =============================================================================
// Form Field Schemas (field definitions for intake forms)
// =============================================================================
export const FieldTypeSchema = z.enum([
    "text",
    "textarea",
    "select",
    "checkbox",
    "radio",
    "date",
    "email",
    "phone",
    "number",
    "file",
]);
export const FormFieldSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    formId: z.string().uuid(),
    fieldType: z.string().max(50),
    label: z.string().min(1).max(255),
    placeholder: z.string().max(255).optional().nullable(),
    options: z.array(z.string()).optional().nullable(),
    isRequired: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: z.string().datetime(),
});
export const CreateFormFieldSchema = z.object({
    formId: z.string().uuid(),
    fieldType: z.string().min(1).max(50),
    label: z.string().min(1).max(255),
    placeholder: z.string().max(255).optional(),
    options: z.array(z.string()).optional(),
    isRequired: z.boolean().optional(), // defaults to false in DB
    sortOrder: z.number().int().optional(), // defaults to 0 in DB
});
export const UpdateFormFieldSchema = z.object({
    fieldType: z.string().min(1).max(50).optional(),
    label: z.string().min(1).max(255).optional(),
    placeholder: z.string().max(255).optional().nullable(),
    options: z.array(z.string()).optional().nullable(),
    isRequired: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
// =============================================================================
// Form Submission Schemas (submitted form responses)
// =============================================================================
export const FormSubmissionSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    formId: z.string().uuid(),
    appointmentId: z.string().uuid().optional().nullable(),
    clientId: z.string().uuid().optional().nullable(),
    dogId: z.string().uuid().optional().nullable(),
    responses: z.record(z.string(), z.unknown()),
    submittedAt: z.string().datetime(),
});
export const CreateFormSubmissionSchema = z.object({
    formId: z.string().uuid(),
    appointmentId: z.string().uuid().optional(),
    clientId: z.string().uuid().optional(),
    dogId: z.string().uuid().optional(),
    responses: z.record(z.string(), z.unknown()).refine((obj) => Object.keys(obj).length > 0, { message: "Responses cannot be empty" }),
});
export const UpdateFormSubmissionSchema = z.object({
    responses: z.record(z.string(), z.unknown()).refine((obj) => Object.keys(obj).length > 0, { message: "Responses cannot be empty" }).optional(),
    appointmentId: z.string().uuid().optional().nullable(),
    clientId: z.string().uuid().optional().nullable(),
    dogId: z.string().uuid().optional().nullable(),
});
//# sourceMappingURL=schemas.js.map